import {
	Dialog,
	DialogPanel,
	Transition,
	TransitionChild,
} from '@headlessui/react'
import { Fragment, useState } from 'react'
import { Link, useLocation } from 'react-router'
import type { routeTypes } from '#app/root'
import { type Item, cn } from '#app/utils/misc'
import type { Theme } from '#app/utils/theme.server'
// import { usePRDData } from '#app/utils/useProjectData'
import type { useOptionalUser } from '#app/utils/user'
import { AppSidebar } from './app-sidebar'
import BreadcrumbsImpl from './breadcrumbs-impl'
import Footer from './footer'
import { NavBar } from './nav-bar'
import { Logo } from './nav-logo'
import { SidebarItems } from './sidebar-items'
import { type NavItem, internalNavItems, navItems } from './sidebar-items-data'
import { Icon } from './ui/icon'
import { Separator } from './ui/separator'
import {
	SidebarInset,
	SidebarProvider,
	// SidebarToggleButton,
} from './ui/sidebar'

export type RootUser = ReturnType<typeof useOptionalUser>

export function Layout({
	allProjects,
	children,
	routeType,
	theme,
	user,
	initialSidebarOpen,
}: {
	allProjects: Item[]
	children: React.ReactNode
	routeType: (typeof routeTypes)[number]
	theme: null | Theme
	user?: null | RootUser
	initialSidebarOpen?: boolean
}) {
	const [sidebarOpen, setSidebarOpen] = useState(false)
	const location = useLocation()
	// const { prd } = usePRDData() ?? {}
	const { prd } = { prd: null }
	let appNavItems: NavItem[]

	// always show the navbar
	// but only show pricing+contact us if on marketing site

	// if logged in, show the project switcher (or just show the user dropdown)

	// if screen is small enough, show the sidebar
	// if logged in and on internal page, show all the app stuff

	if (['auth', 'marketing', 'other', 'site'].includes(routeType)) {
		appNavItems = navItems.marketing
	} else if (routeType === 'internal') {
		appNavItems = internalNavItems('')
	} else {
		appNavItems = navItems.marketing
	}

	const navItemsWithCurrent = appNavItems.map((item) => ({
		...item,
		current: item.href
			? item.href === '/'
				? location.pathname === item.href
				: location.pathname.startsWith(item.href)
			: false,
	}))

	const regex = /^\/projects\/[a-z0-9]+\/pages\/[a-z0-9]+$/
	const isPageEditor = regex.test(location.pathname)

	if (routeType === 'internal') {
		return (
			<SidebarProvider
				defaultOpen={initialSidebarOpen}
				// NO --sidebar-width override here: the width's single source is
				// SIDEBAR_WIDTH in ui/sidebar.tsx (the provider spreads `style`
				// AFTER its own var, so an override here silently wins — this one
				// pinned the sidebar to 18rem and made constant edits look like
				// they did nothing, QA 2026-07-16).
				style={
					{
						'--header-height': 'calc(var(--spacing) * 12)',
					} as React.CSSProperties
				}
			>
				<AppSidebar theme={theme} variant="inset" />
				<SidebarInset>{children}</SidebarInset>
			</SidebarProvider>
		)
	}

	// The public website (handle.chrome === 'site') brings its own header,
	// <main> and footer through SiteShell. Wrapping it in this layout's <main>
	// would nest landmarks (header/footer lose their banner/contentinfo roles).
	if (routeType === 'site') {
		return <>{children}</>
	}

	return (
		<>
			<div className="h-full w-full">
				{
					<>
						{routeType !== 'auth' && (
							<NavBar
								isLoggedIn={!!user}
								routeType={routeType}
								setSidebarOpen={setSidebarOpen}
								theme={theme}
							/>
						)}
						<Transition as={Fragment} show={sidebarOpen}>
							<Dialog
								as="div"
								className="relative z-50 xl:hidden"
								onClose={setSidebarOpen}
							>
								<TransitionChild
									as={Fragment}
									enter="transition-opacity ease-linear duration-300"
									enterFrom="opacity-0"
									enterTo="opacity-100"
									leave="transition-opacity ease-linear duration-300"
									leaveFrom="opacity-100"
									leaveTo="opacity-0"
								>
									<div className="fixed inset-0 bg-black/10" />
								</TransitionChild>

								<div className="fixed inset-0 flex">
									<TransitionChild
										as={Fragment}
										enter="transition ease-in-out duration-300 transform"
										enterFrom="-translate-x-full"
										enterTo="translate-x-0"
										leave="transition ease-in-out duration-300 transform"
										leaveFrom="translate-x-0"
										leaveTo="-translate-x-full"
									>
										<DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1 ">
											<TransitionChild
												as={Fragment}
												enter="ease-in-out duration-300"
												enterFrom="opacity-0"
												enterTo="opacity-100"
												leave="ease-in-out duration-300"
												leaveFrom="opacity-100"
												leaveTo="opacity-0"
											>
												<div className="absolute left-full top-0 flex w-16 justify-center pt-5">
													<button
														className="-m-2.5 p-2.5"
														onClick={() => setSidebarOpen(false)}
														type="button"
													>
														<span className="sr-only">Close sidebar</span>
														<Icon
															aria-hidden="true"
															className="text-primary"
															name="x"
															size="lg"
														/>
													</button>
												</div>
											</TransitionChild>
											{/* Sidebar component, swap this element with another sidebar if you like */}
											<MainArea
												allProjects={allProjects}
												navItems={navItemsWithCurrent}
												routeType={routeType}
												setSidebarOpen={setSidebarOpen}
												user={user}
											/>
										</DialogPanel>
									</TransitionChild>
								</div>
							</Dialog>
						</Transition>
					</>
				}

				<div className={cn('h-full')}>
					<main
						className={cn('h-full')}
						style={{
							paddingTop:
								routeType !== 'auth' ? 'var(--navbar-height, 0px)' : undefined,
						}}
					>
						{children}
					</main>
					{routeType !== 'auth' && (
						<div className="">
							{/* <Footer /> */}
							{/* <Separator className="w-full" /> */}
						</div>
					)}
				</div>
			</div>
		</>
	)
}

function MainArea({
	allProjects,
	navItems,
	routeType,
	setSidebarOpen,
}: {
	allProjects: Item[]
	navItems: NavItem[]
	routeType?: 'internal' | string
	setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
	user?: null | RootUser
}) {
	// const { prd } = { prd: null }
	// const user = useOptionalUser()
	return (
		<div
			className="flex grow flex-col gap-y-5 overflow-y-auto bg-background px-4 pt-4 ring-1 ring-white/10"
			onClick={() => {
				setSidebarOpen(false)
			}}
			onKeyUp={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					setSidebarOpen(false)
				}
			}}
		>
			<div className="-mt-0.5">
				<Link to={'/'}>
					<Logo />
				</Link>
			</div>
			<nav className="flex flex-1 flex-col">
				{/* <SidebarItems
					allProjects={allProjects}
					navItems={navItems}
					prd={prd}
					setSidebarOpen={setSidebarOpen}
					tier={user?.tenant.tier ?? ''}
				/> */}
			</nav>
		</div>
	)
}
